import React, { useEffect, useState } from "react";
import S from "./strings";
import Spectrum from "./Spectrum";
import { isRtlLanguage, monthYear } from "./locale";
import useUiLanguage from "./useUiLanguage";
import { coachingPersonalityService } from "../../../services/coachingPersonalityApi";

// COACHING PERSONALITY — the outer card, on the full profile, for a club.
//
// ABSENCE RENDERS NOTHING, AND THIS IS THE FILE THAT HAS TO MEAN IT.
// GET /users/coaching-personality/card/<account_id>/ answers 404 identically
// for all five reasons a card is not served: no such account, no coach or staff
// profile, no result, the switch off, or the viewer's club holding no pitch
// from this person (coaching_personality_views.py:23-36). This component
// therefore returns null for every one of them, and for a network failure, and
// while the request is in flight. No badge, no placeholder, no "no profile"
// line, no empty frame that later collapses. A club may not learn that a hidden
// portrait exists, so the difference between "there is none" and "you may not
// see it" must not be visible from here either.
//
// FIXED SIZE (Konzept 4a): "a compact card in the profile grid, a defined
// maximum height at every width. It never dominates the profile and never
// unfolds endlessly." The cap is 340px, from the accepted prototype. The only
// things allowed to be shortened to hold it are the axis name and the short
// reading, the two uncritical labels; the detail view behind the magnifier
// shows both in full. No statement, no likert label and no level text appears
// on this card at all, so the ellipsis ban has nothing here to violate.
//
// WHAT THE DETAIL VIEW DOES NOT SHOW: the expanded level texts. The prototype
// prints them; Konzept 4a does not allow them outside ("no explanatory texts,
// no scores") and the payload does not carry them (card_payload_for_club sets
// include_texts=False). The concept and the contract agree, so the detail view
// is the same five readings, larger.

const ZoomIcon = () => (
  <>
    <span className="absolute top-[6px] left-[6px] w-[10px] h-[10px] border-2 border-[#6B7280] rounded-full block" />
    <span className="absolute top-[16px] left-[16px] w-[6px] h-[2px] bg-[#6B7280] rounded-[1px] block origin-left rotate-45" />
  </>
);

const DetailView = ({ card, asOfDate, rtl, onClose }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[rgba(17,24,39,0.45)]"
    onClick={onClose}
    role="presentation"
  >
    <div
      dir={rtl ? "rtl" : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={S.sectionTitle}
      className="bg-white rounded-[14px] w-full max-w-[600px] max-h-[85vh] overflow-y-auto shadow-[0_20px_50px_rgba(17,24,39,0.25)]"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="sticky top-0 bg-white flex items-center gap-[10px] px-6 py-5 border-b border-[#E5E7EB]">
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-bold text-[#111827]">
            {S.sectionTitle}
          </div>
          {/* Three registered sentences, three elements, dots as markup. */}
          <div className="mt-[2px] flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#6B7280]">
            <span>{S.asOf(asOfDate)}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{S.tagSelfAssessment}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{S.tagBigFive}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="min-h-[36px] px-[14px] rounded-[8px] border border-[#E5E7EB] bg-white text-[#374151] text-[13px] font-semibold flex-none"
        >
          {S.close}
        </button>
      </div>

      {(card.axes || []).map((axis) => (
        <div key={axis.axis} className="px-6 py-[18px] border-b border-[#E5E7EB]">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <div className="text-[14px] font-bold text-[#111827] flex-1 min-w-[160px]">
              {axis.name}
            </div>
            <div className="text-[13px] font-semibold text-[#111827]">
              {axis.label}
            </div>
          </div>
          <div className="flex justify-between gap-3 mt-[10px] text-[11px] text-[#6B7280]">
            <span className="max-w-[48%] min-w-0">{axis.pole_left}</span>
            <span className="max-w-[48%] min-w-0 text-end">{axis.pole_right}</span>
          </div>
          <Spectrum position={axis.marker_position} size="zoom" rtl={rtl} />
        </div>
      ))}

      <div className="px-6 py-[14px] text-[11px] text-[#9CA3AF]">
        {S.zoomFooter}
      </div>
    </div>
  </div>
);

// TWO WAYS TO NAME THE SAME PERSON, ONE CARD.
// A staff profile is reached at /search/staff/:staffId, whose id IS the account
// id. A coach profile is reached at /search/coach/:coachId, whose id is a
// TheSports id, and that page has no account id to give: the unauthenticated
// public coach payload withholds it on purpose, next to email, phone number and
// address (users/serializers.py PublicCoachSerializer, "Remove sensitive
// fields"). So the backend serves the card on two routes and this component
// takes whichever id its page holds. Both routes run the identical visibility
// check and answer the identical 404 (coaching_personality_views.py:474).
//
// Neither given, or a coach with no TheSports link at all: no request is made
// and nothing renders. That is the absence the concept asks for, not an error.
//
// `className` lands on the card itself and NOT on a wrapper: a wrapper would
// still occupy its margin when there is no card, and an empty box with spacing
// around it is exactly the "there is something here" hint that absence is not
// allowed to render.
const CoachingPersonalityCard = ({ accountId, tsCoachId, className = "" }) => {
  const language = useUiLanguage();
  const rtl = isRtlLanguage(language);
  const [card, setCard] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCard(null);
    // The account id wins when a page happens to hold both: it names the person
    // directly, while the TheSports id has to be resolved through the Coach row.
    const request = accountId
      ? coachingPersonalityService.getCard(accountId)
      : tsCoachId
      ? coachingPersonalityService.getCardByCoachId(tsCoachId)
      : null;
    if (!request) return undefined;
    request.then((response) => {
      if (cancelled) return;
      setCard(response.success ? response.data : null);
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, tsCoachId]);

  if (!card || !(card.axes || []).length) return null;

  const asOfDate = monthYear(card.as_of, language);

  return (
    <>
      <div
        dir={rtl ? "rtl" : undefined}
        className={`bg-white border border-[#E5E7EB] rounded-[6px] p-5 max-h-[340px] overflow-hidden flex flex-col ${className}`}
      >
        <div className="flex items-center gap-[10px]">
          <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[#374151] flex-1 min-w-0 truncate">
            {S.sectionTitle}
          </div>
          <span className="text-[10px] text-[#6B7280] bg-[#F5F7FB] border border-[#E5E7EB] rounded-full px-2 py-[3px] flex-none whitespace-nowrap">
            {S.tagSelfAssessment}
          </span>
          <button
            type="button"
            onClick={() => setZoomed(true)}
            aria-label={S.zoomAria}
            title={S.zoomTitle}
            className="w-[28px] h-[28px] rounded-[8px] border border-[#E5E7EB] bg-white relative flex-none p-0 hover:border-[#2183FE]"
          >
            <ZoomIcon />
          </button>
        </div>

        <div className="flex flex-col gap-[13px] mt-4">
          {card.axes.map((axis) => (
            <div key={axis.axis}>
              <div className="flex justify-between items-baseline gap-[10px] text-[12px]">
                {/* The two uncritical labels: one line each so the card holds
                    its maximum height. Both read in full one click away. */}
                <span className="text-[#6B7280] min-w-0 truncate">
                  {axis.name}
                </span>
                <span className="text-[#111827] font-semibold text-end min-w-0 truncate">
                  {axis.label}
                </span>
              </div>
              <Spectrum position={axis.marker_position} size="card" rtl={rtl} />
            </div>
          ))}
        </div>

        <div className="mt-[14px] text-[11px] text-[#6B7280]">
          {S.asOf(asOfDate)}
        </div>
      </div>

      {zoomed && (
        <DetailView
          card={card}
          asOfDate={asOfDate}
          rtl={rtl}
          onClose={() => setZoomed(false)}
        />
      )}
    </>
  );
};

export default CoachingPersonalityCard;
